import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { isWithinInterval, startOfDay, endOfDay, subYears, format } from 'date-fns';
import { CustomerPurchaseHistory } from '../../main/model/CustomerTypes';
import CustomerApiAgent from '../../main/agent/CustomerApiAgent';
import { useCustomerDetailPageContext } from '../datacontroller/CustomerDetailPageContext';
import { toDateText } from '@/common/util/formatUtil';
import DateRangePickerButton from '@/common/view/date-range-picker/button';
import Icon from '@/common/view/icons';
import Button from '@/common/view/button';
import { parseYYYYMMDD } from '../util/dateUtil';
import TableSkeleton from '@/common/view/TableSkeleton';

interface GroupedPurchase {
  id: string;
  purchase_date: string;
  purchase_department_name: string;
  items: CustomerPurchaseHistory[];
}

interface ColumnConfig {
  key: string;
  label: string;
  width: number;
  minWidth: number;
  align: 'left' | 'center' | 'right';
  resizable: boolean;
  flexGrow?: number;
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'expand', label: '상세', width: 60, minWidth: 60, align: 'center', resizable: false },
  { key: 'date', label: '일자', width: 120, minWidth: 80, align: 'center', resizable: true },
  { key: 'department', label: '매장', width: 150, minWidth: 100, align: 'left', resizable: true, flexGrow: 1 },
  { key: 'product', label: '상품', width: 200, minWidth: 100, align: 'left', resizable: true, flexGrow: 2 },
  { key: 'quantity', label: '수량', width: 80, minWidth: 60, align: 'center', resizable: true },
  { key: 'amount', label: '금액', width: 150, minWidth: 100, align: 'right', resizable: true },
  { key: 'note', label: '비고', width: 400, minWidth: 150, align: 'left', resizable: true },
];

const TableWrapper = styled.div`
  border: 1px solid var(--gray-30);
  border-radius: 6px;
  overflow: auto;
  background: #fff;
  max-height: 506px;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: fit-content;
`;

const TableHeader = styled.div`
  display: flex;
  background: var(--gray-10);
  border-bottom: 1px solid var(--gray-30);
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-50);
  position: sticky;
  top: 0;
  z-index: 1;
`;

const HeaderCell = styled.div<{ $align: string; $width: number; $flexGrow?: number }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $width }) => $width}px;
  min-width: ${({ $width }) => $width}px;
  flex-grow: ${({ $flexGrow }) => $flexGrow || 0};
  padding: 8px 10px;
  box-sizing: border-box;
  user-select: none;
`;

const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: transparent;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: rgba(52, 152, 219, 0.3);
  }
`;

const TableBody = styled.div``;

const GroupRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  border-bottom: 1px solid var(--gray-30);
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background: #fff;

  &:hover {
    background: ${({ $clickable }) => ($clickable ? '#f7f7fa' : '#fff')};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ItemRow = styled.div`
  display: flex;
  border-bottom: 1px solid var(--gray-30);
  background: #fafafa;

  &:last-child {
    border-bottom: none;
  }
`;

const DataCell = styled.div<{ $align: string; $width: number; $flexGrow?: number }>`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ $align }) => ($align === 'center' ? 'center' : $align === 'right' ? 'flex-end' : 'flex-start')};
  width: ${({ $width }) => $width}px;
  min-width: ${({ $width }) => $width}px;
  flex-grow: ${({ $flexGrow }) => $flexGrow || 0};
  padding: 13px 10px;
  box-sizing: border-box;
  font-size: 14px;
  color: #575757;
  word-break: break-word;
  line-height: 1.5;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #8e8e93;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #8e8e93;
  font-size: 14px;
`;

const getDefaultDateRange = (): [Date, Date] => {
  const today = new Date();
  const oneYearAgo = subYears(today, 1);
  return [oneYearAgo, today];
};

export const CustomerPurchaseHistoryTable: React.FC = () => {
  const { customerId } = useCustomerDetailPageContext();
  const [dates, setDates] = useState<[Date, Date] | undefined>(getDefaultDateRange);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    DEFAULT_COLUMNS.reduce((acc, col) => {
      acc[col.key] = col.width;
      return acc;
    }, {} as Record<string, number>),
  );
  const [resizedColumns, setResizedColumns] = useState<Set<string>>(new Set());
  const { data: purchaseHistory, isLoading } = CustomerApiAgent.useGetCustomerPurchaseHistory(customerId);

  const resizingRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((e: React.MouseEvent, key: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { key, startX: e.clientX, startWidth: currentWidth };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const { key, startX, startWidth } = resizingRef.current;
      const delta = moveEvent.clientX - startX;
      const column = DEFAULT_COLUMNS.find((col) => col.key === key);
      const minWidth = column?.minWidth || 50;
      const newWidth = Math.max(minWidth, startWidth + delta);
      setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
      setResizedColumns((prev) => new Set(prev).add(key));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const filteredPurchaseHistory = useMemo(() => {
    if (!purchaseHistory) return [];
    if (!dates) return purchaseHistory;

    const [startDate, endDate] = dates;
    return purchaseHistory.filter((purchase) => {
      const purchaseDate = parseYYYYMMDD(purchase.purchase_date);
      if (!purchaseDate) return false;

      return isWithinInterval(purchaseDate, {
        start: startOfDay(startDate),
        end: endOfDay(endDate),
      });
    });
  }, [purchaseHistory, dates]);

  const groupedData = useMemo(() => {
    const grouped = new Map<string, CustomerPurchaseHistory[]>();

    filteredPurchaseHistory.forEach((item) => {
      const parsedDate = parseYYYYMMDD(item.purchase_date);
      if (!parsedDate) return;

      const dateKey = format(parsedDate, 'yyyy-MM-dd');
      const key = `${dateKey}_${item.purchase_department_name}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });

    const result: GroupedPurchase[] = [];
    grouped.forEach((items, key) => {
      const sortedItems = [...items].sort((a, b) => {
        if (b.purchase_amount !== a.purchase_amount) {
          return b.purchase_amount - a.purchase_amount;
        }
        return a.product_name.localeCompare(b.product_name);
      });

      result.push({
        id: key,
        purchase_date: sortedItems[0].purchase_date,
        purchase_department_name: sortedItems[0].purchase_department_name,
        items: sortedItems,
      });
    });

    return result.sort((a, b) => b.purchase_date.localeCompare(a.purchase_date));
  }, [filteredPurchaseHistory]);

  const handleExpand = (group: GroupedPurchase) => {
    if (group.items.length === 1) return;

    const key = group.id;
    setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  const getColumnWidth = (key: string) => columnWidths[key] || DEFAULT_COLUMNS.find((c) => c.key === key)?.width || 100;
  const getColumnFlexGrow = (key: string) => {
    if (resizedColumns.has(key)) return undefined;
    return DEFAULT_COLUMNS.find((c) => c.key === key)?.flexGrow;
  };

  const renderGroupRow = (group: GroupedPurchase) => {
    const isExpanded = expandedRowKeys.includes(group.id);
    const hasMultipleItems = group.items.length > 1;
    const totalQuantity = group.items.reduce((sum, item) => sum + item.purchase_quantity, 0);
    const totalAmount = group.items.reduce((sum, item) => sum + item.purchase_amount, 0);
    const notes = [...new Set(group.items.map((item) => item.additional_information).filter(Boolean))].join('\n');

    const parsedDate = parseYYYYMMDD(group.purchase_date);
    const dateDisplay = parsedDate ? toDateText(parsedDate, 'yyyy-MM-dd') : '-';

    const productDisplay = hasMultipleItems
      ? `${group.items[0].product_name} 외 ${group.items.length - 1}건`
      : group.items[0].product_name;

    return (
      <GroupRow key={group.id} $clickable={hasMultipleItems} onClick={() => handleExpand(group)}>
        <DataCell $align="center" $width={getColumnWidth('expand')} $flexGrow={getColumnFlexGrow('expand')}>
          {hasMultipleItems ? (
            <ExpandIcon $expanded={isExpanded}>▶</ExpandIcon>
          ) : (
            <span style={{ color: '#c5c5c7' }}>-</span>
          )}
        </DataCell>
        <DataCell $align="center" $width={getColumnWidth('date')} $flexGrow={getColumnFlexGrow('date')}>
          {dateDisplay}
        </DataCell>
        <DataCell $align="left" $width={getColumnWidth('department')} $flexGrow={getColumnFlexGrow('department')}>
          {group.purchase_department_name}
        </DataCell>
        <DataCell $align="left" $width={getColumnWidth('product')} $flexGrow={getColumnFlexGrow('product')}>
          {productDisplay}
        </DataCell>
        <DataCell $align="center" $width={getColumnWidth('quantity')} $flexGrow={getColumnFlexGrow('quantity')}>
          {totalQuantity}
        </DataCell>
        <DataCell $align="right" $width={getColumnWidth('amount')} $flexGrow={getColumnFlexGrow('amount')}>
          {formatAmount(totalAmount)}
        </DataCell>
        <DataCell $align="left" $width={getColumnWidth('note')} $flexGrow={getColumnFlexGrow('note')}>
          {notes || '-'}
        </DataCell>
      </GroupRow>
    );
  };

  const renderItemRows = (group: GroupedPurchase) => {
    if (!expandedRowKeys.includes(group.id)) return null;

    return group.items.map((item, index) => (
      <ItemRow key={`${group.id}_${index}`}>
        <DataCell $align="center" $width={getColumnWidth('expand')} $flexGrow={getColumnFlexGrow('expand')} />
        <DataCell $align="center" $width={getColumnWidth('date')} $flexGrow={getColumnFlexGrow('date')} />
        <DataCell $align="left" $width={getColumnWidth('department')} $flexGrow={getColumnFlexGrow('department')} />
        <DataCell $align="left" $width={getColumnWidth('product')} $flexGrow={getColumnFlexGrow('product')}>
          {item.product_name}
        </DataCell>
        <DataCell $align="center" $width={getColumnWidth('quantity')} $flexGrow={getColumnFlexGrow('quantity')}>
          {item.purchase_quantity}
        </DataCell>
        <DataCell $align="right" $width={getColumnWidth('amount')} $flexGrow={getColumnFlexGrow('amount')}>
          {formatAmount(item.purchase_amount)}
        </DataCell>
        <DataCell $align="left" $width={getColumnWidth('note')} $flexGrow={getColumnFlexGrow('note')}>
          -
        </DataCell>
      </ItemRow>
    ));
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative inline-flex gap-2">
        <DateRangePickerButton
          Icon={Icon.Calendar}
          value={dates}
          placement="topStart"
          placeholder="연.월.일 ~ 연.월.일"
          onChange={(dates) => {
            if (!dates) return;
            setDates(dates);
          }}
        />
        {dates && (
          <Button
            skin="gray-outlined"
            size="small"
            onClick={() => {
              setDates(getDefaultDateRange());
            }}>
            초기화
          </Button>
        )}
      </div>

      <TableWrapper>
        <TableContainer>
          <TableHeader>
            {DEFAULT_COLUMNS.map((col) => (
              <HeaderCell key={col.key} $align={col.align} $width={getColumnWidth(col.key)} $flexGrow={col.flexGrow}>
                {col.label}
                {col.resizable && (
                  <ResizeHandle onMouseDown={(e) => handleResizeStart(e, col.key, getColumnWidth(col.key))} />
                )}
              </HeaderCell>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeleton columns={7} rows={5} columnWidths={[0.5, 1, 1.5, 2, 0.8, 1.5, 2]} />
            ) : groupedData.length === 0 ? (
              <EmptyMessage>구매 이력이 없습니다.</EmptyMessage>
            ) : (
              groupedData.map((group) => (
                <React.Fragment key={group.id}>
                  {renderGroupRow(group)}
                  {renderItemRows(group)}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </TableContainer>
      </TableWrapper>
    </div>
  );
};
